/**
 * MailboxesPage — admin-ui mailbox management surface (design spec
 * `plans/designs/tracks-mailbox-ui-design.md`).
 *
 * Reference manager only (spec §8.2 hard gate): this page displays and edits
 * mailbox metadata and the opaque `secretsVaultRef` pointer. Nothing here
 * reads, renders, or transmits mail-account secret material — the vault holds
 * those values and only the email service's worker resolves them.
 *
 * No realtime (spec §2 / platform rule): freshness is manual Refresh +
 * dialog-close cache invalidation; no WebSocket affordance anywhere.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeftRight,
  Inbox,
  Lock,
  Mail,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Send,
  type LucideIcon,
} from 'lucide-react';

import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useToast } from '@/components/shared/Toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  useDisableMailboxMutation,
  useGetMailboxesQuery,
  type MailboxMode,
  type MailboxResponse,
  type MailboxStatus,
} from '@/store/api/mailboxesApi';
import { MailboxFormDialog } from './MailboxFormDialog';

const PAGE_LIMIT = 20; // server default (MAILBOX_DEFAULT_LIMIT); no page-size control yet (spec §3.3)
const ALL = '__all__';

const MODE_META: Record<MailboxMode, { label: string; icon: LucideIcon }> = {
  send: { label: 'Send', icon: Send },
  receive: { label: 'Receive', icon: Inbox },
  both: { label: 'Both', icon: ArrowLeftRight },
};

/** Server params come from the URL query string (spec §1.3 — deep-linkable filters). */
function readUrlFilters(searchParams: URLSearchParams): {
  status?: MailboxStatus;
  mode?: MailboxMode;
} {
  const status = searchParams.get('status');
  const mode = searchParams.get('mode');
  return {
    status: status === 'active' || status === 'disabled' ? status : undefined,
    mode: mode === 'send' || mode === 'receive' || mode === 'both' ? mode : undefined,
  };
}

export function MailboxesPage() {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { status: statusFilter, mode: modeFilter } = useMemo(
    () => readUrlFilters(searchParams),
    [searchParams],
  );

  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [rows, setRows] = useState<MailboxResponse[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MailboxResponse | null>(null);
  const [disableTarget, setDisableTarget] = useState<MailboxResponse | null>(null);

  const {
    data: page,
    isLoading,
    isFetching,
    isError,
    error: listError,
    refetch,
  } = useGetMailboxesQuery({
    limit: PAGE_LIMIT,
    cursor,
    status: statusFilter,
    mode: modeFilter,
  });
  const [disableMailbox, { isLoading: isDisabling }] = useDisableMailboxMutation();

  const isForbidden = (listError as { status?: number } | undefined)?.status === 403;

  // Filter change (server params) resets accumulation — cursor correctness (spec §3.2/E3).
  useEffect(() => {
    setRows([]);
    setCursor(undefined);
  }, [statusFilter, modeFilter]);

  // Accumulate pages (CustomersPage idiom).
  useEffect(() => {
    if (!page) return;
    setRows((previous) => {
      if (!cursor) return page.data;
      const seen = new Set(previous.map((row) => row.id));
      return [...previous, ...page.data.filter((row) => !seen.has(row.id))];
    });
  }, [page, cursor]);

  function setUrlFilter(key: 'status' | 'mode', value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === ALL) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  }

  function clearFilters() {
    setSearch('');
    setSearchParams(new URLSearchParams(), { replace: true });
  }

  const filtersActive = Boolean(search.trim() || statusFilter || modeFilter);
  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    // Client-side filter over accumulated pages — the list API has no search
    // param and its query is `.strict()`-style; never send one (spec §3.2).
    if (!query) return rows;
    return rows.filter((row) => row.address.toLowerCase().includes(query));
  }, [rows, search]);

  async function confirmDisable() {
    if (!disableTarget) return;
    try {
      await disableMailbox(disableTarget.id).unwrap();
      toast.success('Mailbox disabled');
      setDisableTarget(null);
    } catch (err) {
      // Dialog stays open for retry — never lose context (spec §7/E1).
      toast.error(
        (err as { data?: { message?: string } }).data?.message ??
          'Could not disable this mailbox. Try again.',
      );
    }
  }

  if (isForbidden) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Mailboxes</h1>
        </div>
        <EmptyState
          icon={<Lock className="h-10 w-10 text-muted-foreground" />}
          title="You don't have access to mailboxes"
          description="Your role does not include the mailbox list permission. Ask an administrator for email:mailbox-list."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header — exact CustomersPage idiom (spec §1.1). The header CTA and the
          empty-state CTA open the same dialog; CustomersPage ships both in the
          same view too (repo precedent over the one-CTA guideline when empty). */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Mailboxes</h1>
          <p className="text-sm text-muted-foreground">
            Incoming and outgoing mail accounts. Connection values live in the vault — this page
            manages references only.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Create mailbox
        </Button>
      </div>

      {/* Toolbar (spec §3.2) */}
      <div className="flex flex-wrap gap-3">
        <div className="min-w-56 flex-1">
          <Label htmlFor="mailbox-search">Search address</Label>
          <Input
            id="mailbox-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filter loaded addresses…"
          />
        </div>
        <div className="w-40">
          <Label htmlFor="mailbox-status">Status</Label>
          <Select
            value={statusFilter ?? ALL}
            onValueChange={(value) => setUrlFilter('status', value)}
          >
            <SelectTrigger id="mailbox-status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Label htmlFor="mailbox-mode">Mode</Label>
          <Select value={modeFilter ?? ALL} onValueChange={(value) => setUrlFilter('mode', value)}>
            <SelectTrigger id="mailbox-mode">
              <SelectValue placeholder="All modes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All modes</SelectItem>
              <SelectItem value="send">Send</SelectItem>
              <SelectItem value="receive">Receive</SelectItem>
              <SelectItem value="both">Both</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button
            variant="ghost"
            title="Refresh"
            aria-label="Refresh mailboxes"
            disabled={isFetching}
            onClick={() => {
              void refetch();
            }}
          >
            <RefreshCw
              className={isFetching ? 'h-4 w-4 animate-spin motion-reduce:animate-none' : 'h-4 w-4'}
            />
          </Button>
        </div>
      </div>

      <section aria-busy={isLoading || isFetching}>
        {isLoading && rows.length === 0 ? (
          <LoadingRows label="Loading mailboxes" columns={6} rows={5} />
        ) : isError && rows.length === 0 ? (
          <div
            role="alert"
            className="flex items-center justify-between rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm"
          >
            Could not load mailboxes.
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void refetch();
              }}
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Retry
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Mail className="h-10 w-10 text-muted-foreground" />}
            title="No mailboxes yet"
            description="Mailboxes let this organization send and receive mail. Each one points to a vault entry for its connection details."
            action={
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" /> Create mailbox
              </Button>
            }
          />
        ) : visibleRows.length === 0 ? (
          <EmptyState
            icon={<Mail className="h-10 w-10 text-muted-foreground" />}
            title="No mailboxes match these filters"
            description="Adjust the search or filters to see more of the loaded mailboxes."
            action={
              <Button variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto" role="region" aria-label="Mailboxes table">
                <table className="w-full text-left text-sm">
                  <caption className="sr-only">Mailboxes</caption>
                  <thead className="border-b bg-muted/40">
                    <tr>
                      <th scope="col" className="px-4 py-3 font-medium">
                        Address
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        Mode
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        Protocol
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        Vault reference
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        Updated
                      </th>
                      <th scope="col" className="px-4 py-3 text-right font-medium">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => {
                      const ModeIcon = MODE_META[row.mode]?.icon ?? Mail;
                      const disabled = row.status === 'disabled';
                      return (
                        <tr key={row.id} className="border-b last:border-0 hover:bg-muted/20">
                          <td className="px-4 py-3 font-medium">
                            {disabled ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="cursor-help">{row.address}</span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Disabled mailboxes cannot be edited or re-enabled from this UI.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              row.address
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {/* Mode is a capability, not a status — outline badge (spec §3.1/B1) */}
                            <Badge variant="outline" className="gap-1.5 font-normal">
                              <ModeIcon className="h-3.5 w-3.5" aria-hidden="true" />
                              {MODE_META[row.mode]?.label ?? row.mode}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {row.protocol ? (
                              <Badge variant="secondary">{row.protocol.toUpperCase()}</Badge>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={row.status} />
                          </td>
                          <td className="max-w-[16rem] px-4 py-3">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="block cursor-help truncate font-mono text-xs">
                                    {row.secretsVaultRef}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs break-all font-mono">
                                  {row.secretsVaultRef}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </td>
                          <td className="px-4 py-3">{new Date(row.updatedAt).toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">
                            {disabled ? (
                              // Terminal state: no actions (spec §5.1 — the tooltip on
                              // Address carries the explanation; absence IS the confirmation).
                              <span aria-hidden="true" />
                            ) : (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-10 w-10"
                                    aria-label={`Actions for ${row.address}`}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onSelect={() => setEditTarget(row)}>
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onSelect={() => setDisableTarget(row)}
                                  >
                                    Disable mailbox
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {isError && rows.length > 0 && (
          <div
            role="alert"
            className="mt-3 flex items-center justify-between rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm"
          >
            Could not load mailboxes.
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void refetch();
              }}
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Retry
            </Button>
          </div>
        )}

        {rows.length > 0 && (
          <p className="mt-2 text-xs text-muted-foreground">
            {page?.pagination.totalCount != null
              ? `Showing ${rows.length} of ${page.pagination.totalCount}`
              : `Showing ${rows.length}`}
          </p>
        )}

        {/* Cursor pagination (spec §3.3) — field is `pagination.cursor`, not `nextCursor`. */}
        {page?.pagination.hasMore && (
          <div className="mt-3 flex justify-center">
            <Button
              variant="outline"
              disabled={isFetching}
              onClick={() => setCursor(page.pagination.cursor ?? undefined)}
            >
              {isFetching ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </section>

      <MailboxFormDialog open={createOpen} onOpenChange={setCreateOpen} mode="create" />
      <MailboxFormDialog
        open={Boolean(editTarget)}
        onOpenChange={(open) => !open && setEditTarget(null)}
        mode="edit"
        mailbox={editTarget ?? undefined}
      />

      <ConfirmDialog
        open={Boolean(disableTarget)}
        onOpenChange={(open) => !open && !isDisabling && setDisableTarget(null)}
        title="Disable mailbox?"
        description={`${disableTarget?.address ?? ''} will stop sending and receiving mail immediately. This is reversible only by an operator in the service — not from this UI. The address stays reserved.`}
        confirmLabel="Disable mailbox"
        cancelLabel="Cancel"
        variant="destructive"
        onConfirm={() => {
          void confirmDisable();
        }}
        isLoading={isDisabling}
      />
    </div>
  );
}
